import { useState, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Search, Loader2, Download, Check, ExternalLink, FileText, BookOpen, Zap } from 'lucide-react'
import type { LibraryItem } from '@/data/mock'
import {
  mockEuropePmcResults,
  mockPubMedResults,
  mockSciteResults,
  type EuropePmcResult,
  type PubMedResult,
  type SciteResult,
} from '@/data/mockDiscovery'

type DiscoveryTab = 'epmc' | 'pubmed' | 'scite'

interface DiscoveryModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  claimText: string
  onImportToLibrary?: (item: LibraryItem) => void
}

const tabConfig: { id: DiscoveryTab; label: string; icon: typeof FileText }[] = [
  { id: 'epmc', label: 'Full Text (Europe PMC)', icon: BookOpen },
  { id: 'pubmed', label: 'Abstracts (PubMed)', icon: FileText },
  { id: 'scite', label: 'Smart Citations (scite.ai)', icon: Zap },
]

type AnyResult = EuropePmcResult | PubMedResult | SciteResult

function getResults(tab: DiscoveryTab): AnyResult[] {
  switch (tab) {
    case 'epmc': return mockEuropePmcResults
    case 'pubmed': return mockPubMedResults
    case 'scite': return mockSciteResults
  }
}

function getEvidenceContent(result: AnyResult, tab: DiscoveryTab): { label: string; text: string } {
  switch (tab) {
    case 'epmc':
      return { label: 'Full-Text Snippet', text: (result as EuropePmcResult).snippet }
    case 'pubmed':
      return { label: 'Abstract', text: (result as PubMedResult).abstract }
    case 'scite':
      return { label: 'Citation Statement', text: (result as SciteResult).citationStatement }
  }
}

function highlightKeywords(text: string): React.ReactNode {
  const keywords = ['vitiligo', 'market', 'JAK inhibitor', 'ruxolitinib', '$1.2B', '$1.15', '$1.3 billion', 'CAGR', '18.2%', '$420M']
  const pattern = new RegExp(`(${keywords.map(k => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi')
  const parts = text.split(pattern)

  return parts.map((part, i) => {
    if (keywords.some(k => k.toLowerCase() === part.toLowerCase())) {
      return <mark key={i} className="bg-yellow-200/60 text-inherit rounded-sm px-0.5">{part}</mark>
    }
    return part
  })
}

export function DiscoveryModal({ open, onOpenChange, claimText, onImportToLibrary }: DiscoveryModalProps) {
  const [activeTab, setActiveTab] = useState<DiscoveryTab>('epmc')
  const [selectedResultId, setSelectedResultId] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState(claimText)
  const [isSearching, setIsSearching] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [importedIds, setImportedIds] = useState<Set<string>>(new Set())

  const results = hasSearched ? getResults(activeTab) : []
  const selectedResult = results.find(r => r.id === selectedResultId) ?? null

  const handleSearch = useCallback(() => {
    setIsSearching(true)
    setSelectedResultId(null)
    setTimeout(() => {
      setIsSearching(false)
      setHasSearched(true)
      const firstResult = getResults(activeTab)
      if (firstResult.length > 0) {
        setSelectedResultId(firstResult[0].id)
      }
    }, 1500)
  }, [activeTab])

  const handleTabChange = useCallback((tab: DiscoveryTab) => {
    setActiveTab(tab)
    setSelectedResultId(null)
    if (hasSearched) {
      const tabResults = getResults(tab)
      if (tabResults.length > 0) {
        setSelectedResultId(tabResults[0].id)
      }
    }
  }, [hasSearched])

  const handleImport = useCallback((result: AnyResult) => {
    setImportedIds(prev => new Set(prev).add(result.id))

    const item: LibraryItem = {
      id: `lib-ext-${Date.now()}`,
      filename: `${result.title.slice(0, 60)}...pdf`,
      status: 'indexed',
      workspaceId: 'ws-1',
      title: result.title,
      fileSubtype: 'Publication',
      amaCitation: `${result.authors} ${result.journal}. ${result.year}.`,
      shortCitation: result.journal,
      abstract: 'snippet' in result ? result.snippet : 'abstract' in result ? result.abstract : result.citationStatement,
      doi: result.doi,
      authors: result.authors.split(', ').map(a => a.replace(' et al.', '')),
      source: result.journal,
      therapeuticArea: 'Dermatology',
      isActiveInRag: true,
    }

    onImportToLibrary?.(item)
  }, [onImportToLibrary])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-6xl h-[85vh] p-0 gap-0 flex flex-col">
        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-4 shrink-0 border-b border-border">
          <DialogTitle>Evidence Discovery</DialogTitle>
          <div className="flex gap-2 mt-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search claim text..."
                className="h-10 w-full rounded-md border border-border bg-background pl-9 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
            <Button onClick={handleSearch} disabled={isSearching} className="gap-1.5">
              {isSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Search
            </Button>
          </div>
        </DialogHeader>

        {/* Tab bar */}
        <div className="flex gap-1 px-6 py-2 border-b border-border shrink-0 bg-slate-50/50">
          {tabConfig.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'bg-white text-foreground font-medium shadow-sm border border-border'
                    : 'text-muted-foreground hover:bg-white/60 hover:text-foreground'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {tab.label}
              </button>
            )
          })}
        </div>

        {/* Body */}
        <div className="flex flex-1 min-h-0">
          {/* Left panel — result list */}
          <div className="w-[35%] border-r border-border flex flex-col min-h-0">
            <ScrollArea className="flex-1 min-h-0">
              <div className="p-3 flex flex-col gap-2">
                {isSearching && (
                  <div className="flex flex-col items-center gap-3 py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                    <span className="text-sm text-muted-foreground">
                      Searching {activeTab === 'epmc' ? 'Europe PMC full-text corpus' : activeTab === 'pubmed' ? '35M+ PubMed citations' : 'scite.ai smart citations'}...
                    </span>
                  </div>
                )}

                {!isSearching && !hasSearched && (
                  <div className="flex flex-col items-center gap-3 py-12 text-center px-4">
                    <Search className="h-8 w-8 text-muted-foreground/50" />
                    <p className="text-sm text-muted-foreground">
                      Enter a search query and click Search to find external evidence.
                    </p>
                  </div>
                )}

                {!isSearching && hasSearched && results.map((result) => (
                  <button
                    key={result.id}
                    onClick={() => setSelectedResultId(result.id)}
                    className={`text-left rounded-lg border p-3 transition-all ${
                      selectedResultId === result.id
                        ? 'border-indigo-400 bg-indigo-50/40 ring-1 ring-indigo-300'
                        : 'border-border hover:border-indigo-300 bg-white'
                    }`}
                  >
                    <h4 className="text-sm font-medium text-foreground leading-snug line-clamp-2 mb-1.5">
                      {result.title}
                    </h4>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{result.year}</span>
                      <span className="truncate">{result.journal}</span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-2">
                      {activeTab === 'epmc' && (
                        <Badge className="text-[10px] px-1.5 py-0 bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                          Open Access
                        </Badge>
                      )}
                      {(activeTab === 'epmc' || activeTab === 'scite') && (
                        <Badge className="text-[10px] px-1.5 py-0 bg-blue-100 text-blue-700 hover:bg-blue-100">
                          Snippet Available
                        </Badge>
                      )}
                      {importedIds.has(result.id) && (
                        <Badge className="text-[10px] px-1.5 py-0 bg-emerald-100 text-emerald-700 hover:bg-emerald-100 gap-0.5">
                          <Check className="h-2.5 w-2.5" />
                          Imported
                        </Badge>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </ScrollArea>
          </div>

          {/* Right panel — preview */}
          <div className="flex-1 flex flex-col min-h-0">
            {selectedResult ? (
              <>
                <ScrollArea className="flex-1 min-h-0">
                  <div className="p-6 flex flex-col gap-4">
                    {/* Paper title & meta */}
                    <div>
                      <h3 className="text-lg font-semibold text-foreground leading-snug mb-2">
                        {selectedResult.title}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-1">
                        {selectedResult.authors}
                      </p>
                      <p className="text-sm text-muted-foreground italic mb-2">
                        {selectedResult.journal} ({selectedResult.year})
                      </p>
                      {selectedResult.doi && (
                        <a
                          href={`https://doi.org/${selectedResult.doi}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                        >
                          <ExternalLink className="h-3 w-3" />
                          {selectedResult.doi}
                        </a>
                      )}
                    </div>

                    {/* Evidence content */}
                    {(() => {
                      const { label, text } = getEvidenceContent(selectedResult, activeTab)
                      return (
                        <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-4">
                          <div className="flex items-center gap-1.5 mb-2">
                            <FileText className="h-3.5 w-3.5 text-blue-600" />
                            <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">
                              {label}
                            </span>
                          </div>
                          <p className="text-sm text-slate-700 leading-relaxed">
                            {highlightKeywords(text)}
                          </p>
                        </div>
                      )
                    })()}
                  </div>
                </ScrollArea>

                {/* Import footer */}
                <div className="shrink-0 border-t border-border px-6 py-3 bg-slate-50/50">
                  {importedIds.has(selectedResult.id) ? (
                    <div className="flex items-center gap-1.5">
                      <Check className="h-4 w-4 text-emerald-600" />
                      <span className="text-sm font-medium text-emerald-600">Added to library — re-audit to verify</span>
                    </div>
                  ) : (
                    <Button
                      className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => handleImport(selectedResult)}
                    >
                      <Download className="h-4 w-4" />
                      Import &amp; Verify
                    </Button>
                  )}
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-center px-8">
                <div>
                  <FileText className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">
                    {hasSearched ? 'Select a paper to preview its evidence' : 'Search to discover external evidence'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
